/**
 * Utility functions for array operations.
 */

/**
 * Creates a lookup map from an array of objects.
 * Useful for O(1) lookups instead of O(N) array.find() operations.
 *
 * @param array - The array to convert to a map
 * @param keyFn - Function to extract the key from each item
 * @param valueFn - Function to extract the value from each item (defaults to the item itself)
 * @returns A Record (object) mapping keys to values
 *
 * @example
 * const locations = [{ id: '1', name: 'Location A' }, { id: '2', name: 'Location B' }];
 * const locationNameById = createLookupMap(locations, loc => loc.id, loc => loc.name);
 * // Returns: { '1': 'Location A', '2': 'Location B' }
 */
export function createLookupMap<T, K extends string | number, V>(
  array: T[],
  keyFn: (item: T) => K,
  valueFn?: (item: T) => V,
): Record<K, V> {
  return array.reduce(
    (acc, item) => {
      const key = keyFn(item);
      const value = valueFn ? valueFn(item) : (item as unknown as V);
      acc[key] = value;
      return acc;
    },
    {} as Record<K, V>,
  );
}
